<template>
  <div class="qa-qc-main well-page">

    <TabularLoaders v-if="showLoader" />
    
    <!-- Main Data Table with Color Indicators -->
    <div v-if="data.length > 0" class="qaqc-main-container">
      <!-- Color Indicators at Top -->
      <div class="qaqc-top-indicators">
        <div class="qaqc-indicator-item">
          <div class="qaqc-indicator-dot passed"></div>
          <span>Passed</span>
        </div>
        <div class="qaqc-indicator-item">
          <div class="qaqc-indicator-dot failed"></div>
          <span>Failed</span>
        </div>
        <div class="qaqc-indicator-item">
          <div class="qaqc-indicator-dot pending"></div>
          <span>Pending</span>
        </div>
      </div>
      
      <!-- Table Section -->
      <div class="qaqc-table-section">
        <v-table class="qaqc-main-table">
      <thead>
             <tr class="qaqc-table-header">
               <th class="qaqc-main-header">{{ columnDisplayNames?.fluidSampleID || 'Fluid Sample ID' }}</th>
               <th class="qaqc-main-header">Tests</th>
               <th class="qaqc-main-header">{{ columnDisplayNames?.field || 'Field' }}</th>
               <th class="qaqc-main-header">{{ columnDisplayNames?.reservoir || 'Reservoir' }}</th>
               <th class="qaqc-main-header">{{ columnDisplayNames?.fluidType || 'Fluid Type' }}</th>
               <th class="qaqc-main-header">{{ columnDisplayNames?.fluidClassification || 'Fluid Classification' }}</th>
               <th class="qaqc-main-header">{{ columnDisplayNames?.reservoirPressure || 'Reservoir Pressure' }}</th>
               <th class="qaqc-main-header">{{ columnDisplayNames?.reservoirTemperature || 'Reservoir Temperature' }}</th>
        </tr>
      </thead>
      <tbody class="well-table-content">
        <template v-for="(item, index) in data" :key="index">
              <tr class="qaqc-table-row">
            <td>{{ item.fluidSampleID }}</td>
                         <td>
                  <div class="qaqc-tests-container">
                 <span 
                   v-for="(test, testIndex) in item.testsArray" 
                   :key="testIndex"
                      :class="getTestStatusClass(test, item)"
                   @click="handleTestClick(test, item)"
                 >
                   {{ test.displayName }}
                 </span>
               </div>
             </td>
            <td>{{ item.field }}</td>
            <td>{{ item.reservoir }}</td>
            <td>{{ item.fluidType }}</td>
            <td>{{ item.fluidClassification }}</td>
            <td>{{ formatValueWithUOM(item.reservoirPressure, 'reservoirPressure', item.originalSample) }}</td>
            <td>{{ formatValueWithUOM(item.reservoirTemperature, 'reservoirTemperature', item.originalSample) }}</td>
          </tr>
        </template>
      </tbody>
    </v-table>
       </div>
     </div>


    <!-- Pagination -->
    <div class="qaqc-pagination-container" v-if="totalItems > 0">
      <div class="qaqc-pagination-left">
        <v-select
          v-model="itemsPerPage"
          :items="[10, 20, 50, 100]"
          label="Items per page"
          variant="outlined"
          density="compact"
          class="items-per-page-select"
          @update:model-value="onItemsPerPageChange"
        ></v-select>
      </div>
      
      <div class="qaqc-pagination-center">
        <span class="qaqc-pagination-info">
           {{ startIndex + 1 }}-{{ endIndex }} of {{ totalItems }} Samples
         </span>
       </div>
      
      <div class="qaqc-pagination-right">
        <v-btn
          icon="mdi-chevron-left"
          variant="text"
          :disabled="currentPage === 1"
          @click="previousPage"
          class="qaqc-pagination-btn"
        ></v-btn>
        <v-btn
          icon="mdi-chevron-right"
          variant="text"
          :disabled="currentPage >= totalPages"
          @click="nextPage"
          class="qaqc-pagination-btn"
        ></v-btn>
      </div>
    </div>

    <!-- No Data Messages -->
    <div v-if="data.length === 0 && !showLoader && timeoutMessage === ''" class="no-asset-div">
      <div class="text-center">
        <v-icon color="grey" size="large" class="mb-2">mdi-database-off</v-icon>
        <h4>No data found</h4>
        <p>There is no data available for QA/QC review at this time.</p>
      </div>
    </div>

    <div v-else-if="timeoutMessage !== ''" class="no-asset-div timeout">
      <div class="text-center">
        <v-icon color="warning" size="large" class="mb-2">mdi-alert-circle</v-icon>
        <p>{{ timeoutMessage }}</p>
        <v-btn color="primary" @click="fetchData()" prepend-icon="mdi-reload">
          Retry
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../axiosInterceptor";
import TabularLoaders from "../../components/layout/Loaders/TabularLoaders.vue";
import { getDisplayName } from "../../utils/cache.js";

export default {
  name: "QAQCDetailView",
  components: {
    TabularLoaders,
  },
  data() {
    return {
      activeTab: 'Tests',
      data: [],
      tenantId: localStorage.getItem("tenantId"),
      showLoader: false,
      timeoutMessage: "",
      currentPage: 1,
      itemsPerPage: 20,
      totalItems: 0,
      wellName: null,
      columnDisplayNames: {
        fluidSampleID: 'Fluid Sample ID',
        field: 'Field',
        reservoir: 'Reservoir',
        fluidType: 'Fluid Type',
        fluidClassification: 'Fluid Classification',
        reservoirPressure: 'Reservoir Pressure',
        reservoirTemperature: 'Reservoir Temperature'
      },
      columnMetadata: {},
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.totalItems);
    },
  },

  methods: {
    goBack() {
      this.$router.go(-1);
    },
    
    async loadColumnDisplayNames() {
      try {
        const columnKeys = [
          'fluidSampleID',
          'field', 
          'reservoir',
          'fluidType',
          'fluidClassification',
          'reservoirPressure',
          'reservoirTemperature'
        ];
        
        for (const key of columnKeys) {
          try {
            const result = await getDisplayName('sample', key);
            if (result && result.displayName) {
              this.columnDisplayNames[key] = result.displayName;
              this.columnMetadata[key] = {
                displayName: result.displayName,
                decimals: result.digits || 0
              };
            } else {
              this.columnDisplayNames[key] = this.formatHeaderName(key);
              this.columnMetadata[key] = {
                displayName: this.formatHeaderName(key),
                decimals: 0
              };
            }
          } catch (error) {
            this.columnDisplayNames[key] = this.formatHeaderName(key);
            this.columnMetadata[key] = {
              displayName: this.formatHeaderName(key),
              decimals: 0
            };
          }
        }
      } catch (error) {
        const columnKeys = [
          'fluidSampleID',
          'field', 
          'reservoir',
          'fluidType',
          'fluidClassification',
          'reservoirPressure',
          'reservoirTemperature'
        ];
        
        columnKeys.forEach(key => {
          if (!this.columnDisplayNames[key]) {
            this.columnDisplayNames[key] = this.formatHeaderName(key);
            this.columnMetadata[key] = {
              displayName: this.formatHeaderName(key),
              decimals: 0
            };
          }
        });
      }
    },
    
    getUnitForProperty(key, dataSource) {
      const uomKey = key + '_UOM';
      if (dataSource && dataSource[uomKey]) {
        return dataSource[uomKey];
      }
      
      return "";
    },
    
    formatHeaderName(key) {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    },
    
    formatValueWithUOM(value, key, dataSource) {
      if (value === null || value === undefined || value === '-') {
        return value;
      }
      
      const metadata = this.columnMetadata?.[key];
      const decimals = metadata ? metadata.decimals : 0;
      const uom = this.getUnitForProperty(key, dataSource);
      
      let formattedValue = value;
      if (typeof value === 'number' || !isNaN(parseFloat(value))) {
        formattedValue = parseFloat(value).toFixed(decimals);
      }
      
      if (uom) {
        return `${formattedValue} ${uom}`;
      }
      
      return formattedValue;
    },
    onTabChange() {
      this.currentPage = 1;
      this.fetchData();
    },
    onItemsPerPageChange() {
      this.currentPage = 1;
      this.fetchData();
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetchData();
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.fetchData();
      }
    },
    async fetchData() {
      this.showLoader = true;
      this.timeoutMessage = "";
      
      try {
        await this.loadColumnDisplayNames();
        
        if (this.wellName) {
          await this.fetchWellSamples();
        } else {
          this.data = [];
          this.totalItems = 0;
        }
      } catch (error) {
        this.timeoutMessage = "Failed to load data. Please try again.";
        this.data = [];
        this.totalItems = 0;
      } finally {
      this.showLoader = false;
      }
    },
    async fetchWellSamples() {
      try {
        const response = await api.get(`/public/api/samples?well="${this.wellName}"&viewSummary=true`);

        if (response.data && response.data.data && response.data.data.samples && response.data.data.samples.length > 0) {
          this.mapApiDataToTable(response.data.data.samples);
          if (response.data.data.pagination) {
            this.totalItems = response.data.data.pagination.TotalRecords || response.data.data.samples.length;
          }
        } else {
          this.data = [];
          this.totalItems = 0;
        }
      } catch (error) {
        throw error;
      }
    },
    mapApiDataToTable(apiData) {
      const mappedData = [];
      
      apiData.forEach((sample, index) => {
        const testsArray = this.extractTestsFromSample(sample);
        
        const mappedItem = {
          id: sample.ID || index + 1,
          fluidSampleID: sample.fluidSampleID || sample.uniqueID || `SMP-${index + 1}`,
          testsArray: testsArray,
          field: sample.location?.field || '-',
          reservoir: sample.location?.reservoir || '-',
          fluidType: sample.fluidType || '-',
          fluidClassification: this.getFluidClassification(sample),
          reservoirPressure: this.getReservoirPressure(sample),
          testTemperature: this.getTestTemperature(sample),
          reservoirTemperature: this.getReservoirTemperature(sample),
          tTestSelected: false,
          tResSelected: false,
          qcStatus: this.getQCStatus(sample),
          originalSample: sample
        };
        
        mappedData.push(mappedItem);
      });
      
      const start = this.startIndex;
      const end = start + this.itemsPerPage;
      this.data = mappedData.slice(start, end);
    },
    extractTestsFromSample(sample) {
      const tests = [];
      
      if (sample.compositiontests && sample.compositiontests.length > 0) {
        sample.compositiontests.forEach((test, index) => {
          const testId = test.ID || test._id || test.id;
          tests.push({
            type: 'Composition',
            testType: 'compositiontests',
            TestType: 'compositiontests', 
            test_type: 'compositiontests', 
            testId: testId,
            ID: testId, 
            id: testId, 
            testData: test,
            displayName: this.getCompositionDisplayName(test, sample.compositiontests.length, index),
            index: index,
            contaminationType: test.contaminationType,
            recombinationType: test.recombinationType,
            testTemperature: test.testTemperature,
            testTemperature_UOM: test.testTemperature_UOM
          });
        });
      }
      
      if (sample.ccetests && sample.ccetests.length > 0) {
        sample.ccetests.forEach((test, index) => {
          const testId = test.ID || test._id || test.id;
          tests.push({
            type: 'CCE Test',
            testType: 'ccetests',
            TestType: 'ccetests',
            test_type: 'ccetests',
            testId: testId,
            ID: testId,
            id: testId,
            testData: test,
            displayName: this.getTestDisplayName(test, 'CCE', sample.ccetests.length, index),
            index: index,
            testTemperature: test.testTemperature,
            testTemperature_UOM: test.testTemperature_UOM
          });
        });
      }
      
      if (sample.viscositytests && sample.viscositytests.length > 0) {
        sample.viscositytests.forEach((test, index) => {
          const testId = test.ID || test._id || test.id;
          tests.push({
            type: 'Viscosity Test',
            testType: 'viscositytests',
            TestType: 'viscositytests',
            test_type: 'viscositytests',
            testId: testId,
            ID: testId,
            id: testId,
            testData: test,
            displayName: this.getTestDisplayName(test, 'Viscosity Test', sample.viscositytests.length, index),
            index: index,
            testTemperature: test.testTemperature,
            testTemperature_UOM: test.testTemperature_UOM
          });
        });
      }
      if (sample.dltests && sample.dltests.length > 0) {
        sample.dltests.forEach((test, index) => {
          const testId = test.ID || test._id || test.id;
          tests.push({
            type: 'DL Test',
            testType: 'dltests',
            TestType: 'dltests',
            test_type: 'dltests',
            testId: testId,
            ID: testId,
            id: testId,
            testData: test,
            displayName: this.getTestDisplayName(test, 'DL', sample.dltests.length, index),
            index: index,
            testTemperature: test.testTemperature,
            testTemperature_UOM: test.testTemperature_UOM
          });
        });
      }
      if (sample.cvdtests && sample.cvdtests.length > 0) {
        sample.cvdtests.forEach((test, index) => {
          const testId = test.ID || test._id || test.id;
          tests.push({
            type: 'CVD Test',
            testType: 'cvdtests',
            TestType: 'cvdtests',
            test_type: 'cvdtests',
            testId: testId,
            ID: testId,
            id: testId,
            testData: test,
            displayName: this.getTestDisplayName(test, 'CVD', sample.cvdtests.length, index),
            index: index,
            testTemperature: test.testTemperature,
            testTemperature_UOM: test.testTemperature_UOM
          });
        });
      }
      
      if (sample.separatortests && sample.separatortests.length > 0) {
        sample.separatortests.forEach((test, index) => {
          const testId = test.ID || test._id || test.id;
          tests.push({
            type: 'Separator Test',
            testType: 'separatortests',
            TestType: 'separatortests',
            test_type: 'separatortests',
            testId: testId,
            ID: testId,
            id: testId,
            testData: test,
            displayName: this.getTestDisplayName(test, 'Separator Test', sample.separatortests.length, index),
            index: index,
            testTemperature: test.testTemperature,
            testTemperature_UOM: test.testTemperature_UOM
          });
        });
      }
      if (sample.IsTestAvailable) {
        const testId = sample.ID || sample._id || sample.id;
        tests.push({
          type: 'Sample Validation',
          testType: 'sampleValidation',
          TestType: 'sampleValidation',
          test_type: 'sampleValidation',
          testId: testId,
          ID: testId,
          id: testId,
          testData: sample,
          displayName: 'Sample Validation',
          index: 0
        });
      }
      if (tests.length === 0) {
        const testId = sample.ID || sample._id || sample.id;
        tests.push({
          type: 'Sample Validation',
          testType: 'sampleValidation',
          TestType: 'sampleValidation',
          test_type: 'sampleValidation',
          testId: testId,
          ID: testId,
          id: testId,
          testData: sample,
          displayName: 'Sample Validation',
          index: 0
        });
      }
      
      return tests;
    },
    
    getCompositionDisplayName(test, totalCount, index) {
      let displayName = 'Composition';
      
      if (test.contaminationType || test.recombinationType) {
        displayName += ' (';
        if (test.contaminationType) {
          displayName += test.contaminationType;
        } else if (test.recombinationType) {
          displayName += test.recombinationType;
        }
        displayName += ')';
      }
      
      if (totalCount > 1) {
        displayName += ` (Test #${index + 1})`;
      }
      
      return displayName;
    },
    
    getTestDisplayName(test, testType, totalCount, index) {
      let displayName = test.name || testType;
      
      if (test.testTemperature) {
        const uom = test.testTemperature_UOM || '';
        displayName += ` (${test.testTemperature}${uom ? ` ${uom}` : ''})`;
      } else if (totalCount > 1) {
        displayName += ` (Test #${index + 1})`;
      }
      
      return displayName;
    },
    
    getFluidClassification(sample) {
      if (sample.fluidType === 'Oil') {
        return 'Oil';
      } else if (sample.fluidType === 'Gas') {
        return 'Gas';
      } else if (sample.fluidType) {
        return sample.fluidType;
      }
      return '-';
    },
    getReservoirPressure(sample) {
      if (sample.sampleProperties?.reservoirPressure) {
        return sample.sampleProperties.reservoirPressure;
      }
      return '-';
    },
    getTestTemperature(sample) {
      if (sample.testTemperature != null) {
        return sample.testTemperature;
      }
      if (sample.sampleProperties?.testTemperature != null) {
        return sample.sampleProperties.testTemperature;
      }
      if (sample.standardTemperature != null) {
        return sample.standardTemperature;
      }
      return '-';
    },
    getReservoirTemperature(sample) {
      if (sample.reservoirTemperature) {
        return sample.reservoirTemperature;
      }
      if (sample.sampleProperties?.reservoirTemperature) {
        return sample.sampleProperties.reservoirTemperature;
      }
      if (sample.standardTemperature) {
        return sample.standardTemperature;
      }
      return '-';
    },
    getQCStatus(sample) {
      if (sample.compositiontests && sample.compositiontests.length > 0) {
        return 'Passed';
      }
      return 'Pending';
    },
     
     handleTestClick(test, item) {
       const sampleId = item.originalSample?._id || item.originalSample?.ID || item.id;
       const testId = test.testId;
       
       localStorage.setItem('testId', testId);
       localStorage.setItem('sampleId', sampleId);
       
       window.dispatchEvent(new CustomEvent('sampleIdChanged'));
       
       const testTypeMap = {
         'CCE Test': 'ccetest',
         'DL Test': 'dltest', 
         'CVD Test': 'cvdtest',
         'Viscosity Test': 'viscositytest',
         'Separator Test': 'separatortest',
         'Composition': 'compositiontest',
         'Sample Validation': 'samplevalidation'
       };
       
       const testType = testTypeMap[test.type] || 'ccetest';
       
       this.$router.push({
         name: 'UnifiedTestView',
         params: { 
           testType: testType,
           testId: testId
         },
       });
     },
     
    getQCStatusColor(status) {
      const statusColors = {
        'Passed': 'success',
        'Pending': 'warning'
      };
      return statusColors[status] || 'grey';
    },
    getTestStatusClass(test, item) {
      const sample = item.originalSample;
      if (!sample) {
        return 'test-status-pending clickable-test';
      }
      
      if (test.type === 'Sample Validation') {
        if (sample.IsTestAvailable) {
          return 'test-status-passed clickable-test';
        } else {
          return 'test-status-pending clickable-test';
        }
      }
      
      if (test.testData && test.testData.hasOwnProperty('IsVerified')) {
        const isVerifiedValue = test.testData.IsVerified;
        
        if (isVerifiedValue === true || isVerifiedValue === 1 || isVerifiedValue === '1') {
          return 'test-status-passed clickable-test';
        } else if (isVerifiedValue === false || isVerifiedValue === 0 || isVerifiedValue === '0') {
          return 'test-status-failed clickable-test';
        } else {
          return 'test-status-pending clickable-test';
        }
      }
      
      return 'test-status-pending clickable-test';
    }
  },
  watch: {
    '$route'(to, from) {
      if (to.query.well !== from.query.well) {
        this.wellName = to.query.well || null;
        this.currentPage = 1;
        if (this.wellName) {
          this.fetchData();
        } else {
          this.data = [];
          this.totalItems = 0;
        }
      }
    }
  },
  mounted() {
    this.wellName = this.$route.query.well || null;
    
    if (!this.wellName) {
      const storedWellName = localStorage.getItem('lastSelectedWell');
      if (storedWellName) {
        this.wellName = storedWellName;
        this.$router.replace({
          name: 'QAQCDetailView',
          query: { ...this.$route.query, well: storedWellName }
        });
      }
    }
    
    if (this.wellName) {
      localStorage.setItem('lastSelectedWell', this.wellName);
      this.fetchData();
    } else {
      this.data = [];
      this.totalItems = 0;
    }
  },
};
</script>

<style scoped>
@import '../../assets/Styling/qaqc.css';

.detail-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.back-btn {
  margin-right: 16px;
}

.qa-tabs {
  margin-top: 16px;
  margin-bottom: 16px;
}

.qa-tabs .v-tab {
  font-size: 16px;
  font-weight: 500;
  text-transform: none;
  min-height: 30px;
}

.qa-tabs .v-tab--selected {
  font-weight: 600;
}

.cursor-pointer {
  cursor: pointer;
}

.clickable-cell:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.no-asset-div {
  padding: 48px 16px;
  text-align: center;
}

.no-asset-div h4 {
  margin: 16px 0 8px 0;
  color: #666;
}

.no-asset-div p {
  color: #999;
  margin: 0;
}

.timeout {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
}

.qaqc-main-container {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.qaqc-table-section {
  width: 100%;
  overflow-x: auto;
}

.qaqc-main-table .v-table__wrapper {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  margin: 0 !important;
  padding: 0 !important;
  height: auto !important;
  min-height: 0 !important;
}

.qaqc-main-table .v-table {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  margin: 0 !important;
  padding: 0 !important;
}

.qaqc-main-table thead {
  background: transparent !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  height: auto !important;
  min-height: 0 !important;
}

.qaqc-main-table thead tr {
  background: transparent !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  height: auto !important;
  min-height: 0 !important;
}

.qaqc-main-table thead tr:first-child {
  margin-top: 0 !important;
  padding-top: 0 !important;
  border: none !important;
  height: auto !important;
  min-height: 0 !important;
}

.qaqc-main-table thead tr:first-child th {
  margin-top: 0 !important;
  padding-top: 16px !important;
  border: none !important;
  background: #2298D5 !important;
}

.dark-mode .qaqc-main-table thead tr:first-child th {
  background-color: var(--dark-mode-table-header) !important;
}

.v-table .v-table__wrapper {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  margin: 0 !important;
  padding: 0 !important;
}

.v-table thead {
  background: transparent !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
}

.v-table thead tr {
  background: transparent !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
}

.qaqc-main-table {
  width: 100%;
  min-width: 800px;
}

.qaqc-table-section .v-table {
  background: transparent !important;
}

.qaqc-table-section .v-table .v-table__wrapper {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

.qaqc-table-section .v-table thead {
  background: transparent !important;
}

.qaqc-table-section .v-table thead tr {
  background: transparent !important;
}

.data-management .v-table {
  background: transparent !important;
}

.data-management .v-table .v-table__wrapper {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  margin: 0 !important;
  padding: 0 !important;
}

.qaqc-main-table td {
  word-wrap: break-word;
  word-break: break-word;
  white-space: normal;
}

.qaqc-main-table th:nth-child(1),
.qaqc-main-table td:nth-child(1) {
  width: 120px;
  min-width: 120px;
}



.qaqc-main-table th:nth-child(3),
.qaqc-main-table td:nth-child(3),
.qaqc-main-table th:nth-child(4),
.qaqc-main-table td:nth-child(4),
.qaqc-main-table th:nth-child(5),
.qaqc-main-table td:nth-child(5),
.qaqc-main-table th:nth-child(6),
.qaqc-main-table td:nth-child(6) {
  width: 100px;
  min-width: 100px;
}

.qaqc-main-table th:nth-child(7),
.qaqc-main-table td:nth-child(7),
.qaqc-main-table th:nth-child(8),
.qaqc-main-table td:nth-child(8) {
  width: 120px;
  min-width: 120px;
}

.qaqc-tests-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.qaqc-tests-container span {
  display: inline-block;
  padding: 4px 8px;
  margin: 2px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  text-align: center;
  min-width: fit-content;
}

.qaqc-tests-container span:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.test-status-passed {
  background-color: #e8f5e8;
  color: #2e7d32;
}

.test-status-failed {
  background-color: #ffebee;
  color: #c62828;
}

.test-status-pending {
  background-color: #fff3e0;
  color: #313130;
}

.clickable-test {
  cursor: pointer;
}

.clickable-test:hover {
  opacity: 0.8;
}

.qaqc-main-table td:nth-child(2) {
  min-width: 200px;
  max-width: 300px;
  word-wrap: break-word;
}
</style>
